import React, { useEffect, useMemo, useRef, useState } from "react";
import EmojiPicker from "emoji-picker-react";
import { FaBold, FaItalic, FaStrikethrough, FaSmile } from "react-icons/fa";
import { MdAddCircle } from "react-icons/md";

type Props = {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  maxLength?: number;           // e.g. 1024 for BODY, 60 for HEADER/FOOTER
  showCharCount?: boolean;      // default true if maxLength provided
  disabled?: boolean;
};

const toolbarBtn =
  "inline-flex items-center justify-center px-2 py-1 rounded border bg-white shadow-sm hover:bg-gray-50";

const pickerContainerStyle: React.CSSProperties = {
  position: "absolute",
  zIndex: 50,
  top: "42px",
};

const WhatsAppEditor: React.FC<Props> = ({
  value,
  onChange,
  placeholder = "",
  maxLength,
  showCharCount = true,
  disabled = false,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showEmoji, setShowEmoji] = useState(false);

  // Compute the next placeholder index based on existing {{n}} in the text
  const nextParamIndex = useMemo(() => {
    const matches = Array.from(value.matchAll(/{{(\d+)}}/g)).map((m) =>
      parseInt(m[1], 10)
    );
    const max = matches.length ? Math.max(...matches) : 0;
    return max + 1;
  }, [value]);

  const insertAtCursor = (insertText: string) => {
    if (!textareaRef.current) return;
    const ta = textareaRef.current;
    const start = ta.selectionStart ?? value.length;
    const end = ta.selectionEnd ?? value.length;
    const newVal = value.slice(0, start) + insertText + value.slice(end);

    if (maxLength && newVal.length > maxLength) return; // hard limit
    onChange(newVal);

    // Restore cursor just after inserted text
    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      textareaRef.current.selectionStart = textareaRef.current.selectionEnd =
        start + insertText.length;
      textareaRef.current.focus();
    });
  };

  const wrapSelection = (wrapper: string) => {
    if (!textareaRef.current) return;
    const ta = textareaRef.current;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    const selected = value.slice(start, end);
    const wrapped = `${wrapper}${selected}${wrapper}`;
    const newVal = value.slice(0, start) + wrapped + value.slice(end);

    if (maxLength && newVal.length > maxLength) return;
    onChange(newVal);

    // Keep selection on wrapped content (inside the markers)
    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      textareaRef.current.selectionStart = start + wrapper.length;
      textareaRef.current.selectionEnd = end + wrapper.length;
      textareaRef.current.focus();
    });
  };

  const handleEmojiClick = (emojiData: any) => {
    insertAtCursor(emojiData.emoji);
    setShowEmoji(false);
  };

  const insertParam = () => {
    insertAtCursor(`{{${nextParamIndex}}}`);
  };

  // Close emoji picker when clicking outside
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!textareaRef.current) return;
      const editorWrap = textareaRef.current.closest("[data-wp-editor]");
      if (editorWrap && !editorWrap.contains(target)) setShowEmoji(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const currentLen = value.length;
  const overLimit = !!maxLength && currentLen > maxLength;

  return (
    <div data-wp-editor>
      {/* Toolbar */}
      <div className="flex w-100 gap-2 items-center mb-2 relative">
        <button
          type="button"
          className={toolbarBtn}
          onClick={() => wrapSelection("*")}
          disabled={disabled}
          title="Bold (*text*)"
        >
          <FaBold />
        </button>
        <button
          type="button"
          className={toolbarBtn}
          onClick={() => wrapSelection("_")}
          disabled={disabled}
          title="Italic (_text_)"
        >
          <FaItalic />
        </button>
        <button
          type="button"
          className={toolbarBtn}
          onClick={() => wrapSelection("~")}
          disabled={disabled}
          title="Strikethrough (~text~)"
        >
          <FaStrikethrough />
        </button>

        {/* <div className="relative">
          <button
            type="button"
            className={toolbarBtn}
            onClick={() => setShowEmoji((s) => !s)}
            disabled={disabled}
            title="Insert emoji"
          >
            <FaSmile />
          </button>
          {showEmoji && (
            <div style={pickerContainerStyle}>
              <EmojiPicker onEmojiClick={handleEmojiClick} />
            </div>
          )}
        </div> */}

        <button
          type="button"
          className={toolbarBtn}
          onClick={insertParam}
          disabled={disabled}
          title={`Insert parameter {{${nextParamIndex}}}`}
        >
          <MdAddCircle />
          <span className="ml-1 text-sm">{`{{${nextParamIndex}}} Add Variable`}</span>
        </button>

        {/* <div className="ml-auto text-xs opacity-70">
          WhatsApp formatting: *bold* _italic_ ~strike~
        </div> */}
      </div>

      {/* Textarea */}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(maxLength ? e.target.value.slice(0, maxLength) : e.target.value)}
        placeholder={placeholder}
        className={`w-100 border rounded p-2 ${overLimit ? "border-danger" : "border-gray-300"}`}
        rows={6}
        disabled={disabled}
      />

      {/* Char counter */}
      {maxLength && (showCharCount ?? true) && (
        <div className={`text-end mt-1 text-xs ${overLimit ? "text-danger" : "text-muted"}`}>
          {currentLen} / {maxLength}
        </div>
      )}
    </div>
  );
};

export default WhatsAppEditor;